import { useEffect, useState } from 'react';
import { api } from '../api';
import { sar } from '../format';

interface DecisionCard {
  id: string;
  source_lens: string;
  urgency: 'info' | 'warn' | 'critical';
  amount_sar?: number;
  title: string;
  context: string;
  suggested_action?: string;
  created_at: string;
}

interface BankRow {
  bank: string;
  balance: number;
  account_last4: string;
}

interface TodayPayload {
  lens: 'L01';
  date: string;
  org_name: string;
  today_pnl: number;
  decision_inbox: DecisionCard[];
  anomalies_count: number;
  cash_by_bank: BankRow[];
  _meta: { phase: number; mode: string; source: string };
}

export function LensToday() {
  const [data, setData] = useState<TodayPayload | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    api<TodayPayload>('/lenses/L01/today')
      .then(setData)
      .catch((e) => setErr(String(e)));
  }, []);

  if (err) return <div className="lens error">خطأ: {err}</div>;
  if (!data) return <div className="lens">جارٍ التحميل…</div>;

  return (
    <div className="lens">
      <header>
        <h2>L01 — اليوم</h2>
        <span className="badge">{data.date}</span>
        <span className="badge src">{data._meta.source}</span>
      </header>

      <section className="widgets">
        <div className="widget">
          <h3>Decision Inbox</h3>
          <p className="big">{data.decision_inbox.length}</p>
          <p className="hint">قرارات معلَّقة</p>
        </div>
        <div className="widget">
          <h3>Today P&L</h3>
          <p className="big">{sar(data.today_pnl)}</p>
          <p className="hint">صافي اليوم</p>
        </div>
        <div className="widget">
          <h3>Anomalies</h3>
          <p className="big">{data.anomalies_count}</p>
          <p className="hint">رادار الشذوذ (L12)</p>
        </div>
      </section>

      <section className="panel">
        <h3>القرارات المعلَّقة</h3>
        <ul className="cards">
          {data.decision_inbox.map((card) => (
            <li key={card.id} className={`card u-${card.urgency}`}>
              <div className="card-head">
                <span className={`pill u-${card.urgency}`}>{card.urgency}</span>
                <span className="lens-tag">{card.source_lens}</span>
                {card.amount_sar !== undefined && (
                  <span className="amount">{sar(card.amount_sar)}</span>
                )}
              </div>
              <p className="card-title">{card.title}</p>
              <p className="card-ctx">{card.context}</p>
              {card.suggested_action && (
                <p className="card-action">💡 {card.suggested_action}</p>
              )}
            </li>
          ))}
        </ul>
      </section>

      <section className="panel">
        <h3>السيولة حسب البنك</h3>
        <table className="banks">
          <thead>
            <tr>
              <th>البنك</th>
              <th>الحساب</th>
              <th>الرصيد</th>
            </tr>
          </thead>
          <tbody>
            {data.cash_by_bank.map((b) => (
              <tr key={b.account_last4}>
                <td>{b.bank}</td>
                <td>*{b.account_last4}</td>
                <td className="num">{sar(b.balance)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <footer className="meta">
        <span>org: {data.org_name}</span>
        <span>phase: {data._meta.phase}</span>
        <span>mode: {data._meta.mode}</span>
      </footer>
    </div>
  );
}
